import React from 'react';
import { OptimizedImage } from '@/components/OptimizedImage';
import nightSkyBook from '@/assets/night-sky-photography-book.webp';
import adamWoodworthImg from '@/assets/adam-woodworth.webp';
import photographingNightSkyBook from '@/assets/photographing-night-sky-book.webp';
import alynWallaceImg from '@/assets/alyn-wallace.webp';
import ericBrownImg from '@/assets/eric-d-brown.webp';
import sonyA7ivImg from '@/assets/sony-a7iv.webp';
import sonyA7rivImg from '@/assets/sony-a7riv.webp';
import sonyA6700Img from '@/assets/sony-a6700.webp';
import sony14mmImg from '@/assets/sony-14mm.webp';
import sony24mmImg from '@/assets/sony-24mm.webp';
import sony35mmImg from '@/assets/sony-35mm.webp';
import sony1635mmImg from '@/assets/sony-16-35mm.webp';
import sony50mmImg from '@/assets/sony-50mm.webp';
import sony100400mmImg from '@/assets/sony-100-400mm.webp';
import msmNomadImg from '@/assets/msm-nomad.webp';
import rrsTvc24Img from '@/assets/rrs-tvc24.webp';
import acratechHeadImg from '@/assets/acratech-head.webp';
import adamWoodworthWorkshopImg from '@/assets/adam-woodworth-workshop.webp';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ExternalLink, Camera, BookOpen, Wrench, GraduationCap, Instagram, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEOHead } from '@/components/SEOHead';

const photographers = [
  {
    name: 'Adam Woodworth',
    description: 'One of the most thoughtful astrophotography educators working today. He\'s a software engineer by trade, which shows in how precisely he breaks down the technical side of night sky photography — from planning to capture to processing.',
    url: 'https://www.adamwoodworth.com',
    instagram: 'https://www.instagram.com/awoodworthphoto/',
    image: adamWoodworthImg,
  },
  {
    name: 'Alyn Wallace',
    description: 'One of the most generous educators in astrophotography. His YouTube channel covers everything from planning and shooting to detailed editing walkthroughs, and he had a gift for making advanced techniques feel approachable.',
    note: 'Alyn passed away unexpectedly in 2024, but his channel remains one of the best resources for anyone learning nightscape photography.',
    url: 'https://www.youtube.com/@AlynWallace',
    instagram: 'https://www.instagram.com/alynwallace',
    image: alynWallaceImg,
  },
  {
    name: 'Eric D. Brown',
    description: 'I know... this is kind of selfish, but I have to do it. I\'m the guy behind Milky Way Planner. I shoot landscape astrophotography across the western US and build the tools I wish existed when I was starting out. My work focuses on combining dramatic foreground elements with the night sky — canyons, mountains, and desert formations. I share my process and what I\'m learning along the way.',
    url: 'https://imagesbyericbrown.com/',
    instagram: 'https://www.instagram.com/eric_d_brown/',
    image: ericBrownImg,
  },
];

const books = [
  {
    title: 'Night Sky Photography: From First Principles to Professional Results',
    author: 'Adam Woodworth',
    description: 'A solid resource for anyone serious about improving their nightscape work. He also runs workshops where he teaches hands-on, and he\'s been using planning tools like MWP to help his students find the best shooting windows.',
    url: 'https://www.amazon.com/gp/product/1781577501/ref=as_li_qf_asin_il_tl?ie=UTF8&tag=adamwoodworth-20&creative=9325&linkCode=as2&creativeASIN=1781577501&linkId=7c70e340e234afee7e25817734a78b73',
    image: nightSkyBook,
  },
  {
    title: 'Photographing the Night Sky',
    author: 'Alyn Wallace',
    description: 'The most comprehensive guide to landscape astrophotography I\'ve come across — 570 pages covering everything from gear and settings to multi-exposure techniques and post-processing. Alyn had a way of making complex topics accessible, whether you\'re just starting out or refining your workflow. Alyn passed away unexpectedly in 2024, and this memorial 2nd edition features a new cover and several pages devoted to him. A book every astrophotographer should have on their shelf.',
    url: 'https://alynwallacephotography.com/shop/photographing-night-sky-astrophotography-book',
    image: photographingNightSkyBook,
  },
];

const gearCategories = [
  {
    name: 'Cameras',
    items: [
      { name: 'Sony a7 IV', description: 'My primary workhorse for astrophotography. 33MP full-frame sensor with incredible low-light performance and 15+ stops of dynamic range.', url: 'https://www.amazon.com/dp/B09PWV1GQS?tag=milkywayplanner-20', note: undefined as string | undefined, image: sonyA7ivImg },
      { name: 'Sony a7 IV Astro Modified', description: 'My dedicated astrophotography body, modified to pass Ha light. The sensor mod dramatically improves hydrogen-alpha nebula capture.', url: 'https://www.spencerscamera.com/astro-conversions.cfm', note: undefined as string | undefined, image: sonyA7ivImg },
      { name: 'Sony a7R IV', description: '61MP high-resolution sensor for detailed landscape and Milky Way shots. Incredible for large prints and pixel-peeping details.', url: 'https://www.amazon.com/dp/B07VGHW91J?tag=milkywayplanner-20', note: undefined as string | undefined, image: sonyA7rivImg },
      { name: 'Sony a6700', description: 'Compact APS-C powerhouse with BIONZ XR and 4K 120p. Great travel and backup camera for astrophotography sessions.', url: 'https://www.amazon.com/dp/B0CBN8Y77Z?tag=milkywayplanner-20', note: undefined as string | undefined, image: sonyA6700Img },
    ],
  },
  {
    name: 'Lenses',
    items: [
      { name: 'Sony 14mm f/1.8 GM', description: 'My go-to ultra-wide for Milky Way core shots. f/1.8 aperture and sagittal flare control make it purpose-built for night skies.', url: 'https://www.amazon.com/dp/B09311DQ6P?tag=milkywayplanner-20', note: undefined as string | undefined, image: sony14mmImg },
      { name: 'Sony 24mm f/1.4 GM', description: 'Sharp corner-to-corner even wide open. Fast f/1.4 aperture is a huge advantage for stars — great focal length for Milky Way arches.', url: 'https://www.amazon.com/dp/B07HJXVHSS?tag=milkywayplanner-20', note: undefined as string | undefined, image: sony24mmImg },
      { name: 'Sony 16-35mm f/2.8 GM', description: 'Versatile wide-angle zoom. The constant f/2.8 aperture gives flexibility to adjust composition without losing exposure.', url: 'https://www.amazon.com/dp/B071LHLS11?tag=milkywayplanner-20', note: 'Mark I version — optically excellent for astrophotography.', image: sony1635mmImg },
      { name: 'Sony 35mm f/1.4 GM', description: 'Perfect focal length for Milky Way core with foreground separation. f/1.4 aperture pulls in an incredible amount of light.', url: 'https://www.amazon.com/dp/B08SWMH186?tag=milkywayplanner-20', note: undefined as string | undefined, image: sony35mmImg },
      { name: 'Sony 50mm f/1.4 GM', description: 'Ideal for isolating parts of the Milky Way or shooting galaxy-rich regions. Beautiful rendering wide open.', url: 'https://www.amazon.com/dp/B0BWFSJXQC?tag=milkywayplanner-20', note: undefined as string | undefined, image: sony50mmImg },
      { name: 'Sony 100-400mm f/4.5-5.6 GM OSS', description: 'Versatile telephoto zoom for deep-sky widefield and lunar photography. Sharp across the range with excellent stabilization.', url: 'https://amzn.to/4usDItb', note: undefined as string | undefined, image: sony100400mmImg },
    ],
  },
  {
    name: 'Accessories',
    items: [
      { name: 'Move Shoot Move Nomad', description: 'The most portable star tracker I\'ve found. Small enough to fit in a jacket pocket, no polar scope needed — just point it north and go. I use it for widefield tracked shots when I don\'t want to carry a full equatorial mount. Great for travel and backpacking trips where every ounce counts.', url: 'https://www.moveshootmove.com/products/nomad-diy-kit', image: msmNomadImg },
      { name: 'Really Right Stuff TVC-24 Versa Series MK2', description: 'Rock solid carbon fiber tripod that handles wind and uneven terrain without flinching. It\'s not cheap, but for astrophotography where any vibration ruins a long exposure, stability is everything. I\'ve used this on rocky ridgelines and sandy desert floors and it doesn\'t let me down.', url: 'https://amzn.to/46YCsUz', image: rrsTvc24Img },
      { name: 'Acratech Panoramic Head', description: 'Lightweight, precise, and perfect for nightscape panoramas. When you\'re stitching multi-frame Milky Way shots, smooth and repeatable panning matters — this head delivers that without adding a lot of weight to your kit.', url: 'https://amzn.to/4blDOtJ', image: acratechHeadImg },
    ],
  },
];

const workshops = [
  {
    name: 'Adam Woodworth Photography Workshops',
    instructor: 'Adam Woodworth',
    description: 'Adam runs small-group workshops across the northeast — from Milky Way sessions on Maine\'s Bold Coast to iceberg chasing in Newfoundland and fall colors in Cape Breton. His teaching style is patient, precise, and covers everything from field technique to post-processing in Lightroom and Photoshop. Alumni consistently highlight the personalized attention and how much they learn in a few days.',
    url: 'https://www.adamwoodworth.com/workshops',
    image: adamWoodworthWorkshopImg,
  },
];

const Resources: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Astrophotography Resources — Gear, Books, Photographers"
        description="Curated astrophotography resources — best cameras, lenses, star trackers, books, and photographers to follow, personally recommended by an astrophotographer."
        keywords="astrophotography gear, best camera for astrophotography, best lens for milky way, star tracker astrophotography, astrophotography books, night sky photography gear, astrophotography equipment, best tripod for astrophotography"
        canonicalUrl="/resources"
      />
        {/* Hero */}
        <section className="bg-slate-950 py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <span className="eyebrow text-gold-light mb-4 block">Resources</span>
            <h1 className="text-3xl md:text-4xl text-white tracking-[-0.03em] mb-4">
              Recommended Resources
            </h1>
            <p className="text-lg text-white/70 leading-relaxed max-w-2xl mx-auto">
              Everything here is something I personally use, have read, or can vouch for. No filler — just the stuff that's actually helped me become a better astrophotographer.
            </p>
          </div>
        </section>

        {/* Photographers to Follow */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-2">
            <Camera className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Photographers to Follow</h2>
          </div>
          <p className="text-muted-foreground text-sm mb-5">
            These photographers consistently inspire me and share knowledge generously with the community.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {photographers.map((p) => (
              <Card key={p.name} className="flex flex-col">
                <CardContent className="p-5 flex flex-col flex-1">
                  {p.image && (
                    <a href={p.url} target="_blank" rel="noopener noreferrer">
                      <OptimizedImage src={p.image} alt={`Astrophotography by ${p.name}`} width={384} className="w-full h-40 object-cover rounded-md mb-3 hover:opacity-90 transition-opacity cursor-pointer" />
                    </a>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-foreground">{p.name}</h3>
                    <div className="flex items-center gap-2">
                      {p.instagram && (
                        <a href={p.instagram} target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:text-pink-600 transition-colors">
                          <Instagram className="w-5 h-5" />
                        </a>
                      )}
                      <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-foreground transition-colors">
                        <ExternalLink className="w-4.5 h-4.5" />
                      </a>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm flex-1 mb-4">{p.description}</p>
                  {p.note && (
                    <p className="text-muted-foreground/70 text-xs italic mb-4">Note: {p.note}</p>
                  )}
                  <a href={p.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="w-full">
                      Visit Website
                      <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
          </div>
        </section>

        {/* Books */}
        <section className="section-alt py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Books</h2>
          </div>
          <p className="text-muted-foreground text-sm mb-5">
            The books that have had the biggest impact on my astrophotography journey.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {books.map((b) => (
              <Card key={b.title} className="flex flex-col">
                <CardContent className="p-5 flex flex-col flex-1">
                  {b.image && (
                    <a href={b.url} target="_blank" rel="noopener noreferrer">
                      <OptimizedImage src={b.image} alt={b.title} width={384} className="w-full h-auto aspect-square object-contain rounded-md mb-3 hover:opacity-90 transition-opacity cursor-pointer" />
                    </a>
                  )}
                  <h3 className="font-semibold text-foreground mb-1">{b.title}</h3>
                  <p className="text-muted-foreground text-xs mb-2">by {b.author}</p>
                  <p className="text-muted-foreground text-sm flex-1 mb-4">{b.description}</p>
                  <a href={b.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="w-full">
                      Buy on Amazon
                      <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
          </div>
        </section>

        {/* Workshops */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-2">
            <GraduationCap className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Workshops</h2>
          </div>
          <p className="text-muted-foreground text-sm mb-5">
            Investing in education accelerated my growth faster than any piece of gear.
          </p>

          {/* MWP Workshop Directory — internal */}
          <Link to="/workshops" className="group block mb-8 p-6 sm:p-8 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Camera className="w-5 h-5 text-gold" />
                  <h3 className="text-lg font-semibold text-white">Milky Way Planner Workshop Directory</h3>
                </div>
                <p className="text-sm text-white/65 leading-relaxed max-w-xl">
                  Browse astrophotography workshops at dark sky locations worldwide. Search by location, view instructor profiles, and find sessions near your next shoot.
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-shrink-0">
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gold group-hover:text-gold-light transition-colors">
                  Browse Workshops <ArrowRight className="w-3.5 h-3.5" />
                </span>
                <Link to="/workshops/instructors" className="text-xs text-white/50 hover:text-white/80 transition-colors" onClick={(e) => e.stopPropagation()}>
                  View all instructors →
                </Link>
              </div>
            </div>
          </Link>

          {/* External workshops from friends */}
          <p className="text-muted-foreground text-xs uppercase tracking-wider font-semibold mb-4">From photographers I trust</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {workshops.map((w) => (
              <Card key={w.name} className="flex flex-col">
                <CardContent className="p-5 flex flex-col flex-1">
                  {'image' in w && w.image && (
                    <a href={w.url} target="_blank" rel="noopener noreferrer">
                      <OptimizedImage src={w.image} alt={`${w.instructor} workshop`} width={384} className="w-full h-40 object-cover rounded-md mb-3 hover:opacity-90 transition-opacity cursor-pointer" />
                    </a>
                  )}
                  <h3 className="font-semibold text-foreground mb-1">{w.name}</h3>
                  <p className="text-muted-foreground text-xs mb-2">by {w.instructor}</p>
                  <p className="text-muted-foreground text-sm flex-1 mb-4">{w.description}</p>
                  <a href={w.url} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="w-full">
                      View Workshops
                      <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                    </Button>
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
          </div>
        </section>

        {/* Gear */}
        <section className="section-alt py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-2 mb-2">
            <Wrench className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold text-foreground">Gear</h2>
          </div>
          <p className="text-muted-foreground text-sm mb-5">
            What I actually take into the field. I care more about reliability and results than specs; if it's on this list, it's because I've used it and trust it.
          </p>
          <div className="space-y-8">
            {gearCategories.map((category) => (
              <div key={category.name}>
                <h3 className="text-lg font-medium text-foreground mb-3">{category.name}</h3>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {category.items.map((item) => (
                    <Card key={item.name} className="flex flex-col">
                      <CardContent className="p-5 flex flex-col flex-1">
                        {'image' in item && item.image && (
                          <a href={item.url} target="_blank" rel="noopener noreferrer">
                            <OptimizedImage src={item.image} alt={item.name} width={384} className="w-full h-40 object-contain rounded-md mb-3 hover:opacity-90 transition-opacity cursor-pointer" />
                          </a>
                        )}
                        <h3 className="font-semibold text-foreground mb-2">{item.name}</h3>
                        <p className="text-muted-foreground text-sm flex-1 mb-4">{item.description}</p>
                        {'note' in item && item.note && (
                          <p className="text-muted-foreground/70 text-xs italic mb-4">{item.note}</p>
                        )}
                        <a href={item.url} target="_blank" rel="noopener noreferrer">
                          <Button variant="outline" size="sm" className="w-full">
                            Buy on Amazon
                            <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
                          </Button>
                        </a>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
          </div>
        </section>

        {/* Affiliate Disclosure */}
        <section className="py-8">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <p className="text-muted-foreground text-xs max-w-2xl">
              Some links on this page are affiliate links. I only recommend products I personally use and trust. Purchasing through these links helps support Milky Way Planner at no extra cost to you.
            </p>
          </div>
        </section>
    </div>
  );
};

export default Resources;
